/* eslint-disable functional/immutable-data */
import type { ReactElement } from 'react';
import React, { useCallback, useMemo } from 'react';

import type { PressableStateCallbackType as UIEventState } from 'react-native';
import { View, Pressable, StyleSheet } from 'react-native';
import { useStyle } from 'react-native-style-utilities';
import { webOnlyStyles } from '../../utils';
import { BodyText } from '../Text';

import type { ChipProps, ChipState } from './Chip.types';
import { ChipText, ChipBadge, ChipBox, Menu } from './subcomponents';

export const Chip = (props: ChipProps): ReactElement => {
  const {
    value,
    selectedValue,
    badge,
    label,
    accessibilityRole = 'button',
    accessibilityState = {},
    accessibilityLabel,
    testID,
    disabled = false,
    children,
    height,
    style,
    maxFontSizeMultiplier,
    onChange,
  } = props;

  const active = useMemo<boolean>(
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    () => (props.active || selectedValue ? selectedValue === value : false),
    [selectedValue, props.active, value],
  );

  const heightStyle = useStyle(() => ({ height }), [height]);

  const getComputedTransitionState = useMemo(
    () =>
      (hovered: boolean): ChipState => {
        if (disabled) return 'disabled';

        if (hovered) return 'hovered';

        if (active) return 'active';

        return 'default';
      },
    [active, disabled],
  );

  const handlePress = useCallback(() => {
    if (value && onChange && !disabled) onChange(value);
  }, [onChange, value, disabled]);
  const a11yLabel = `${accessibilityLabel} ${disabled ? 'disabled' : 'button'}`;

  accessibilityState.disabled = Boolean(disabled);

  return (
    <Pressable
      disabled={disabled}
      accessibilityHint={a11yLabel}
      accessibilityLabel={a11yLabel}
      accessibilityState={{ ...accessibilityState }}
      accessibilityRole={accessibilityRole}
      testID={testID}
      onPress={handlePress}
      style={[{ ...webOnlyStyles({ outline: 0 }) }, heightStyle]}
    >
      {({ hovered = false, focused }: UIEventState) => {
        const state = getComputedTransitionState(hovered);

        return (
          <View testID="chip-wrapper" style={styles.wrapper}>
            {/* ---- Badge Section ---- */}
            {badge ? (
              <View style={styles.badgePosition}>
                <ChipBadge />
              </View>
            ) : undefined}

            {/* ---- Chip Section ----- */}
            <ChipBox style={style} focused={focused} state={state}>
              <ChipText
                state={state}
                maxFontSizeMultiplier={maxFontSizeMultiplier}
              >
                {children}
              </ChipText>
            </ChipBox>
            {/* ---- Label Section ----- */}
            {label?.text ? (
              <View testID="chip-label" style={styles.label}>
                <BodyText size={5}>{label.text}</BodyText>
              </View>
            ) : null}
            {label?.spacing ? <View style={styles.emptyLabel} /> : null}
          </View>
        );
      }}
    </Pressable>
  );
};

const styles = StyleSheet.create({
  badgePosition: {
    position: 'absolute',
    right: 3,
    zIndex: 1,
    top: 0,
  },
  label: {
    paddingTop: 8,
    alignItems: 'center',
  },
  emptyLabel: {
    marginTop: 24,
  },
  wrapper: {
    flex: 1,
    position: 'relative',
  },
});

Chip.Menu = Menu;
